import React, { useState, memo, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon, TrashIcon, ViewBoardIcon, ViewListIcon } from '~/assets/icons';
import styles from './taskList.module.scss';
import classNames from 'classnames/bind';
import BaseTable from '~/components/common/base-table/BaseTable';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import MenuOption from '~/components/jobs/menu-option/MenuOption';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { handleGetTaskBoard, handleGetTaskList } from '~/thunks/task/taskThunk';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { ITask } from '~/utils/interface/task';
import { BaseImage, BaseTab, BaseTabList } from '~/components/common';
import { taskActions } from '~/thunks/task/taskSlice';
import StatusLabelTask from '~/components/jobs/status-label-task/StatusLabelTask';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { Button } from '@mui/material';
import { DIALOG_MODE_CREATE } from '~/utils/constants/common';
import TaskBoard from './taskBoard';
import { JOB_BOARD, JOB_LIST } from '~/utils/constants/jobs';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import taskApi from '~/apis/task';

const cx = classNames.bind(styles);

type TaskListProps = {
  id: string;
  onOpen: (id: string) => void;
};
type ViewModeState = 'list' | 'board';

function TaskList(props: TaskListProps) {
  //#region Destructuring Props
  //#endregion Destructuring Props
  //#region Declare Hook
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  const { t } = useTranslation('admin/job');
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'name',
        label: t(`properties.taskName`),
        sortable: true,
        searchable: true,
      },
      {
        id: 'dueDate',
        label: t(`properties.dueDate`),
        sortable: true,
        searchable: true,
      },
      {
        id: 'assigneeName',
        label: t(`properties.assignedTo`),
        sortable: true,
        searchable: true,
      },
      {
        id: 'jobName',
        label: t(`properties.project`),
        sortable: true,
        searchable: true,
      },
      {
        id: 'description',
        label: t(`properties.note`),
        sortable: true,
        searchable: true,
      },
      {
        id: 'status',
        label: t(`properties.status`),
        sortable: true,
        searchable: true,
      },
      {
        id: '',
        label: '',
        sortable: false,
        searchable: false,
      },
    ],
    [t]
  );
  //#endregion Declare Hook
  //#region Selector
  const { filterList, currentPage, totalPages, needRefreshData } = useAppSelector((state) => state.task);
  //#endregion Selector
  //#region Declare State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectId, setSelectId] = useState<string>('');
  const [taskList, setTaskList] = useState<Array<ITask>>([]);
  const [viewMode, setViewMode] = useState<ViewModeState>('list');
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  //#endregion Declare State
  //#region Implement Hook
  useEffect(() => {
    const handleOpenEdit = async () => {
      try {
        if (viewMode === 'list') {
          const response = await dispatch(handleGetTaskList({ taskId: props.id, data: filterList })).unwrap();
          setTaskList(response.responses);
        } else {
          await dispatch(handleGetTaskBoard({ id: props.id, params: filterList })).unwrap();
        }
      } catch (e) {
        handleApiError(e);
      }
    };
    handleOpenEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id, filterList, needRefreshData, viewMode]);
  //#endregion Implement Hook
  //#region Handle Function

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectId(itemId);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditItem = () => {
    handleClose();
    props.onOpen(selectId);
  };

  const handleDeleteItem = () => {
    if (selectId !== undefined) {
      setSelectedIds([selectId]);
      setOpenDeleteDialog(true);
    }
    handleClose();
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      taskActions.setFilterList({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      taskActions.setFilterList({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };
  const handleChangeViewMode = (_event: React.ChangeEvent<{}>, newValue: ViewModeState) => {
    setViewMode(newValue);
  };

  const handleMenuItemClick = () => {
    dispatch(taskActions.changeMode(DIALOG_MODE_CREATE));
    dispatch(taskActions.changeOpenModalEdit(true));
  };

  const handleDeleteTasks = async () => {
    try {
      await taskApi.deleteTasks({ ids: selectedIds });
      notify('success', t('deleteSuccessMessage'));
      dispatch(taskActions.changeNeedRefreshData(true));
      dispatch(taskActions.changeOpenModalDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    } finally {
      setOpenDeleteDialog(false);
    }
  };
  //#endregion Handle Function

  return (
    <Box className={cx('task-list-container')}>
      <Box className='flex justify-end mb-[20px]'>
        {permissions[PERMISSIONS.projectTask_delete] && (
          <Button
            className={cx(
              'mr-[10px]',
              { 'visibility: visible': selectedIds.length > 0 },
              { 'visibility: hidden': selectedIds.length <= 0 }
            )}
            onClick={() => {
              setOpenDeleteDialog(true);
            }}
            variant='contained'
            color='error'
            disableElevation
            startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
          >
            {t('delete')}
          </Button>
        )}
        <BaseTabList
          value={viewMode}
          onChange={handleChangeViewMode}
          TabIndicatorProps={{ style: { backgroundColor: 'transparent' } }}
        >
          <BaseTab
            key='list'
            value={'list'}
            label={t('list')}
            iconPosition='start'
            icon={
              viewMode === JOB_LIST ? (
                <ViewListIcon className='w-[17px] h-[17px]' stroke='#054DFF' />
              ) : (
                <ViewListIcon className='w-[17px] h-[17px]' />
              )
            }
          />
          <BaseTab
            key='board'
            value={'board'}
            label={t('board')}
            iconPosition='start'
            icon={
              viewMode === JOB_BOARD ? (
                <ViewBoardIcon className='w-[17px] h-[17px]' stroke='#054DFF' />
              ) : (
                <ViewBoardIcon className='w-[17px] h-[17px]' />
              )
            }
          />
        </BaseTabList>
        {permissions[PERMISSIONS.projectTask_create] && (
          <Button
            className='w-[107px] h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
            variant='contained'
            disableElevation
            onClick={handleMenuItemClick}
          >
            {t('addNew')}
          </Button>
        )}
      </Box>
      {viewMode === JOB_LIST ? (
        <BaseTable<ITask>
          data={taskList}
          setSelectedIds={setSelectedIds}
          headCells={headCells}
          onChangePage={handleChangePage}
          onRequestSort={handleRequestSort}
          page={currentPage}
          sort={filterList.sort}
          totalPages={totalPages}
          renderCell={(task: ITask) => {
            return (
              <>
                <TableCell component='th' scope='row' padding='none'>
                  <Box className={cx('task-name-container')}>
                    <Box className={cx('taskName')}>{t(`addTask.${task.name}`)}</Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box className={cx('created-by-container')}>
                    <Box className={cx('name')}>
                      {task?.dueDate ? dayjs(task.dueDate).format('DD MMM, YYYY hh:mm a') : ''}
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box className={cx('task-container')}>
                    {task.assignees?.[0]?.avatar && (
                      <BaseImage src={task.assignees[0].avatar} className={cx('image')} />
                    )}
                    <Box className={cx('task-name-container')}>
                      <Box className={cx('accountName')}>{task.assignees?.[0]?.name}</Box>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box className={cx('created-by-container')}>
                    <Box className={cx('name')}>{task.jobName}</Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box className={cx('created-by-container')}>
                    <Box className={cx('name')}>{task.description}</Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box className={cx('status-container')}>
                    {task?.status && <StatusLabelTask variant={task.statusDetail} />}
                  </Box>
                </TableCell>

                {(permissions[PERMISSIONS.projectTask_delete] || permissions[PERMISSIONS.projectTask_edit]) && (
                  <TableCell>
                    <Box className={cx('deadline-container')}>
                      <IconButton
                        onClick={(event) => {
                          openOptionMenu(event, task.id);
                        }}
                      >
                        <MenuVerticalIcon className='w-[19px] h-[19px]' />
                      </IconButton>
                    </Box>
                  </TableCell>
                )}
              </>
            );
          }}
        />
      ) : (
        <TaskBoard />
      )}

      {(!!permissions[PERMISSIONS.projectTask_edit] || !!permissions[PERMISSIONS.projectTask_delete]) && (
        <MenuOption
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          handleEditItem={handleEditItem}
          handleDeleteItem={handleDeleteItem}
          enableEdit={!!permissions[PERMISSIONS.projectTask_edit]}
          enableDelete={!!permissions[PERMISSIONS.projectTask_delete]}
        />
      )}

      <ModalAlertDelete
        open={openDeleteDialog}
        description={t('alertDescriptionTask')}
        onCancel={() => {
          setOpenDeleteDialog(false);
        }}
        onConfirm={handleDeleteTasks}
      />
    </Box>
  );
}

export default memo(TaskList);
